import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getIn } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import classNames from 'classnames';
import { getFormikValue } from '../../../utils/formikFunctions';
import theme from '../../../styles/theme';
import { BaseTextFieldProps } from '@mui/material/TextField/TextField';
import { FormikValues } from 'formik/dist/types';
import { ReactComponent } from 'tss-react/tools/ReactComponent';

const useStyles = makeStyles<Theme>()((theme) => ({
  formControl: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'row',
    minWidth: 120,
  },
  root: {
    '& .MuiButtonBase-root': {
      padding: 0,
    },
    '&:hover .MuiSvgIcon-root': {
      display: 'block',
    },
  },
  closeIcon: {
    '& .MuiSvgIcon-root': {
      width: 12,
      height: 12,
      color: theme.palette.text.primary,
      display: 'none',
    },
  },
  labelWithExplaining: {
    '& .MuiInputLabel-root': {
      left: 35,
    },
  },
  explainingToolTip: {
    '& .MuiSvgIcon-root': {
      color: theme.palette.background.default,
    },
  },
  toolTip: {
    '& .MuiTooltip-tooltip': {
      background: theme.palette.background.default,
      border: `1px solid ${theme.palette.grey[300]}`,
      boxSizing: 'border-box',
      borderRadius: theme.spacing(0.75),
      color: theme.palette.common.black,
      padding: theme.spacing(2),
      maxWidth: theme.spacing(44),
    },
  },
  hidden: {
    display: 'none',
  },
  viewModeWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

interface IFormSelectWrapper extends BaseTextFieldProps {
  additionalOptions?: Array<any>;
  formik: FormikValues | undefined;
  fieldName?: string;
  options?: Array<any>;
  explainingText?: string;
  disableSorting?: boolean;
  disableClear?: boolean;
  disableSearch?: boolean;
  sortBy?: string;
  autoWidth?: boolean;
  label?: string;
  name: string;
  preserveCase?: boolean;
  viewMode?: boolean;
  showSearch?: boolean;
  onChangeCallback?: () => void;
  onChange?: (e: any) => void;
  onClearCallback?: () => void;
  itemWrapperCallback?: (...props: any[]) => ReactComponent<any> | ReactElement;
}

const AppFormSelectWrapper: React.FC<IFormSelectWrapper> = ({
  disabled = false,
  name,
  className = '',
  additionalOptions = [],
  options = [],
  label,
  formik,
  explainingText = undefined,
  disableSorting = false,
  disableClear = false,
  disableSearch = false,
  fieldName = 'name',
  sortBy = fieldName,
  autoWidth = true,
  error = false,
  preserveCase = false,
  onChangeCallback = () => {},
  onChange,
  onClearCallback = () => {},
  viewMode = false,
  showSearch = true,
  itemWrapperCallback,
  ...props
}) => {
  const { classes } = useStyles(theme);
  const [searchText, setSearchText] = useState('');

  const formikValue = useMemo(
    () => getFormikValue(getIn(formik?.values, name)),
    [formik?.values, name],
  );

  const index = useMemo(
    () =>
      [...additionalOptions, ...options]?.findIndex((option) => {
        const optionId = option?.id;

        return optionId === formikValue || optionId === formikValue?.id;
      }),
    [options, additionalOptions, formikValue],
  );

  const sortedOptions = useMemo(() => {
    const mainOptions = !disableSorting
      ? (options ? [...options] : [])?.sort((a, b) =>
          `${a[sortBy]}`.localeCompare(`${b[sortBy]}`),
        )
      : options;
    return [...additionalOptions, ...mainOptions];
  }, [options, additionalOptions, disableSorting, sortBy]);

  const valueOutOfOption = useMemo(() => {
    const hasFormikDefaultValue = index === -1 && formikValue?.name;

    if (hasFormikDefaultValue) {
      return {
        name: formikValue.name,
        disabled: true,
      };
    }

    return null;
  }, [index, formikValue]);

  const value = useMemo(
    () =>
      index === -1 || index === undefined
        ? valueOutOfOption || ''
        : [...additionalOptions, ...options][index],
    [index, valueOutOfOption, options, additionalOptions],
  );

  const renderOptions = useCallback(() => {
    const renderItems = valueOutOfOption
      ? [valueOutOfOption, ...(sortedOptions || [])]
      : sortedOptions;
    return renderItems?.map((item, index) => (
      <MenuItem
        className={classNames({
          [classes.hidden]:
            !containsText(item?.[fieldName], searchText) || item.disabled,
        })}
        key={`${item.id}${item[fieldName]}${index}`}
        value={item}
        id={item.id}>
        {itemWrapperCallback ? itemWrapperCallback(item) : item[fieldName]}
      </MenuItem>
    ));
  }, [valueOutOfOption, sortedOptions, searchText]);

  if (!(options.length + additionalOptions.length)) {
    return (
      <TextField
        fullWidth
        disabled={true}
        label={label}
        className={classes.formControl}
        value={formikValue?.name}
        name={name}
        margin="dense"
        variant="outlined"
        InputLabelProps={{
          shrink: !!value,
        }}
        {...props}
      />
    );
  }

  if (viewMode)
    return (
      <Box className={classes.viewModeWrapper}>
        {label && <Typography variant="text-md-sb">{label}</Typography>}
        <Typography variant="text-md">{value?.[fieldName]}</Typography>
      </Box>
    );

  return (
    <FormControl className={classes.formControl}>
      <TextField
        fullWidth
        select
        label={label}
        disabled={disabled}
        id={name}
        name={name}
        margin="dense"
        variant="outlined"
        value={value}
        onChange={(d: React.ChangeEvent<HTMLInputElement>) => {
          if (onChange) {
            onChange(d);
          } else {
            formik?.handleChange(d);
          }
        }}
        onBlur={formik?.handleBlur}
        error={getIn(formik?.touched, name) && !!getIn(formik?.errors, name)}
        helperText={
          <>
            {getIn(formik?.touched, name) &&
            typeof getIn(formik?.errors, name) === 'string'
              ? getIn(formik?.errors, name)
              : !!getIn(formik?.errors, name)}
          </>
        }
        className={
          !!explainingText && !formikValue
            ? classNames(classes.root, classes.labelWithExplaining, className)
            : classNames(className, classes.root)
        }
        SelectProps={{
          autoWidth,
          MenuProps: {
            autoFocus: false,
            disableScrollLock: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            onMouseOver: (e) => e.stopPropagation(),
          },
          onClose: () => {
            setTimeout(() => (document.activeElement as HTMLElement)?.blur());
          },
        }}
        InputLabelProps={{
          shrink: !!value,
        }}
        InputProps={{
          endAdornment:
            !disabled && formikValue && !disableClear ? (
              <InputAdornment
                position="end"
                style={{ right: 35, position: 'absolute' }}>
                <IconButton
                  disabled={disabled}
                  className={classes.closeIcon}
                  aria-label="remove value"
                  onClick={() => {
                    formik?.setFieldValue(name, null);
                    onClearCallback();
                  }}
                  size="large">
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          startAdornment: explainingText ? (
            <InputAdornment position="start">
              <Tooltip
                title={explainingText}
                classes={{ popper: classes.toolTip }}>
                <IconButton size="large" className={classes.explainingToolTip}>
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
        {...props}>
        {showSearch && (
          <ListSubheader
            style={{
              paddingRight: 16,
              marginRight: 16,
              display: disableSearch ? 'none' : 'block',
            }}>
            <TextField
              onClick={(e) => e.stopPropagation()}
              size={'small'}
              style={{
                backgroundColor: 'white',
              }}
              autoFocus={true}
              variant={'outlined'}
              placeholder="Type to search..."
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onFocus={() => {
                setSearchText('');
              }}
              onChange={(e) => {
                setSearchText(e.target.value);
                onChangeCallback();
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
        )}
        {renderOptions()}
      </TextField>
    </FormControl>
  );
};

export default AppFormSelectWrapper;
